import { api, type Vehicle } from "../api";
import { canProbablyPlay } from "../poster";
import { useLoad } from "../useLoad";

interface Props {
  teamId: string;
  vehicle: Vehicle;
  onClose: () => void;
  onError: (message: string) => void;
}

/**
 * Every race a truck has ever been in. Task 10.8, requirement 8.3.
 *
 * Requirement 8.3 makes a point worth restating because it is the reason this exists at all: "this
 * requires no additional storage — heat participants are already known, so it is an index, not a new
 * data path." The `RAN#` items behind it were written when each result was committed, one per vehicle per
 * heat, carrying the finishing position. So a truck's whole career is a single query, and the footage of
 * each race comes along because requirement 8.1 attached it to that heat.
 *
 * Newest first, and across *all* tournaments — a truck that has raced in four jams shows four jams'
 * worth. That crossing of tournament boundaries is the whole point; a per-tournament view of a vehicle
 * would already be visible from the bracket.
 */
export function VehicleHistory({ teamId, vehicle, onClose, onError }: Props) {
  const { data, loading } = useLoad(
    () => api.vehicleMedia(teamId, vehicle.vehicleId),
    [teamId, vehicle.vehicleId],
    onError,
  );

  const races = data?.races ?? [];
  const wins = races.filter((race) => race.position === 1).length;
  const clips = races.reduce((total, race) => total + race.media.length, 0);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={`${vehicle.name ?? "Truck"} history`}>
      <div className="sheet-inner">
        <div className="row">
          <p className="section-title grow" style={{ margin: 0 }}>
            {vehicle.name ?? "Truck"}
          </p>
          <button className="btn small" onClick={onClose}>
            Close
          </button>
        </div>

        {loading && races.length === 0 && (
          <div className="centre" style={{ padding: 28 }}>
            <div className="spinner" />
          </div>
        )}

        {!loading && races.length === 0 && (
          <p className="muted" style={{ textAlign: "center" }}>
            Hasn't raced yet.
          </p>
        )}

        {races.length > 0 && (
          <p className="muted" style={{ margin: 0 }}>
            {races.length} {races.length === 1 ? "race" : "races"} · {wins}{" "}
            {wins === 1 ? "win" : "wins"}
            {clips > 0 && ` · ${clips} ${clips === 1 ? "clip" : "clips"}`}
          </p>
        )}

        <ul className="list">
          {races.map((race) => (
            <li key={`${race.tournamentId}-${race.round}-${race.index}`} className="vehicle">
              <div className="row">
                <span className={`place${race.position === 1 ? " won" : ""}`}>{race.position}</span>
                <span className="grow">
                  <span className="name">{race.tournamentName ?? "A jam"}</span>
                  <span className="note">
                    Round {race.round} · race {race.index + 1} of {race.fieldSize}-wide ·{" "}
                    {race.advanced ? "advanced" : "eliminated"}
                  </span>
                </span>
              </div>
              {race.media.length > 0 && (
                <div className="strip">
                  {race.media.map((record) => {
                    const variant =
                      record.playbackVariants.find((entry) => canProbablyPlay(entry.contentType)) ??
                      record.playbackVariants[0];
                    return (
                      // Requirement 8.10 again — a poster and a link, never a player that cannot start.
                      // A history list is the wrong place to mount video anyway: a truck with twenty
                      // races would mount twenty players.
                      <a
                        key={record.mediaId}
                        className="strip-item"
                        href={variant?.url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {record.posterUrl ? (
                          <img src={record.posterUrl} alt="" loading="lazy" />
                        ) : (
                          <span aria-hidden="true">🎬</span>
                        )}
                      </a>
                    );
                  })}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
